/**
 * AccessibilityMenu - תפריט נגישות
 * A native, self-built accessibility menu (not a third-party overlay) offering
 * font sizing, high contrast, link highlighting, a readable font and motion off.
 * Mounted once at the app root so it appears on every route (public + authed).
 * Settings live in accessibilityStore and persist to localStorage.
 *
 * WCAG 2.1 AA / ת"י 5568.
 */

import { useEffect, useRef, useState } from 'react'
import {
  Accessibility,
  X,
  Plus,
  Minus,
  Contrast,
  Link2,
  Type,
  Pause,
  RotateCcw,
} from 'lucide-react'
import {
  useAccessibilityStore,
  applyAccessibilitySettings,
  MIN_FONT_SCALE,
  MAX_FONT_SCALE,
} from '../../store/accessibilityStore'

export default function AccessibilityMenu() {
  const [open, setOpen] = useState(false)
  const settings = useAccessibilityStore()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const { fontScale, highContrast, highlightLinks, readableFont, noAnimations } = settings

  // Apply settings to <html> on mount and whenever they change.
  useEffect(() => {
    applyAccessibilitySettings({
      fontScale,
      highContrast,
      highlightLinks,
      readableFont,
      noAnimations,
    })
  }, [fontScale, highContrast, highlightLinks, readableFont, noAnimations])

  // ESC to close + focus management. When the panel opens, move focus into it;
  // when it closes, return focus to the trigger (WCAG 2.4.3).
  useEffect(() => {
    if (!open) return

    const previouslyFocused = document.activeElement as HTMLElement | null
    const firstFocusable = panelRef.current?.querySelector<HTMLElement>(
      'button, [href], input, [tabindex]:not([tabindex="-1"])'
    )
    firstFocusable?.focus()

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        return
      }
      if (e.key !== 'Tab') return
      // Simple focus trap inside the panel.
      const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, [tabindex]:not([tabindex="-1"])'
      )
      if (!focusables || focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      // Return focus to the trigger (or whatever had it before opening).
      ;(triggerRef.current ?? previouslyFocused)?.focus()
    }
  }, [open])

  const fontPct = Math.round(fontScale * 100)

  return (
    <>
      {/* Floating trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="פתיחת תפריט נגישות"
        className="a11y-fab fixed bottom-4 left-4 z-[70] flex items-center justify-center w-14 h-14 rounded-full bg-primary-600 text-white shadow-lg hover:bg-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-300"
      >
        <Accessibility size={28} aria-hidden="true" />
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 z-[80]"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />

          {/* Panel */}
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="a11y-menu-title"
            className="a11y-panel fixed bottom-4 left-4 z-[90] w-[20rem] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] overflow-y-auto bg-white rounded-xl shadow-2xl border border-gray-200 p-5"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 id="a11y-menu-title" className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Accessibility size={20} aria-hidden="true" />
                תפריט נגישות
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="סגירת תפריט נגישות"
                className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>

            {/* Font size */}
            <div className="mb-4">
              <span className="block text-sm font-medium text-gray-700 mb-2">גודל טקסט</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={settings.decreaseFont}
                  disabled={fontScale <= MIN_FONT_SCALE}
                  aria-label="הקטנת טקסט"
                  className="flex-1 flex items-center justify-center gap-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <Minus size={18} aria-hidden="true" />
                </button>
                <span className="w-16 text-center text-sm font-semibold text-gray-900" aria-live="polite">
                  {fontPct}%
                </span>
                <button
                  type="button"
                  onClick={settings.increaseFont}
                  disabled={fontScale >= MAX_FONT_SCALE}
                  aria-label="הגדלת טקסט"
                  className="flex-1 flex items-center justify-center gap-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <Plus size={18} aria-hidden="true" />
                </button>
              </div>
            </div>

            {/* Toggles */}
            <div className="space-y-2">
              <ToggleRow
                icon={<Contrast size={18} aria-hidden="true" />}
                label="ניגודיות גבוהה"
                pressed={highContrast}
                onClick={() => settings.toggle('highContrast')}
              />
              <ToggleRow
                icon={<Link2 size={18} aria-hidden="true" />}
                label="הדגשת קישורים"
                pressed={highlightLinks}
                onClick={() => settings.toggle('highlightLinks')}
              />
              <ToggleRow
                icon={<Type size={18} aria-hidden="true" />}
                label="גופן קריא"
                pressed={readableFont}
                onClick={() => settings.toggle('readableFont')}
              />
              <ToggleRow
                icon={<Pause size={18} aria-hidden="true" />}
                label="עצירת אנימציות"
                pressed={noAnimations}
                onClick={() => settings.toggle('noAnimations')}
              />
            </div>

            {/* Reset */}
            <button
              type="button"
              onClick={settings.reset}
              className="mt-4 w-full flex items-center justify-center gap-2 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <RotateCcw size={18} aria-hidden="true" />
              איפוס הגדרות נגישות
            </button>

            <a
              href="/accessibility"
              className="mt-3 block text-center text-sm text-primary-700 underline hover:text-primary-800"
            >
              להצהרת הנגישות המלאה
            </a>
          </div>
        </>
      )}
    </>
  )
}

function ToggleRow({
  icon,
  label,
  pressed,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  pressed: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={pressed}
      className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
        pressed
          ? 'bg-primary-50 border-primary-500 text-primary-800'
          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
      }`}
    >
      <span className="flex items-center gap-2">
        {icon}
        {label}
      </span>
      <span
        className={`text-xs font-semibold ${pressed ? 'text-primary-700' : 'text-gray-400'}`}
      >
        {pressed ? 'פעיל' : 'כבוי'}
      </span>
    </button>
  )
}
